import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const MAX_SIZE = 5 * 1024 * 1024;
const ALLOWED_EXT = ["pdf", "doc", "docx"] as const;

const uploadSchema = z.object({
  fileName: z.string().min(1).max(200),
  size: z.number().int().positive().max(MAX_SIZE),
});

export const createResumeUpload = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => uploadSchema.parse(data))
  .handler(async ({ data }) => {
    const ext = data.fileName.split(".").pop()?.toLowerCase() ?? "";
    if (!ALLOWED_EXT.includes(ext as (typeof ALLOWED_EXT)[number])) {
      throw new Error("Formato de arquivo não suportado. Envie PDF, DOC ou DOCX.");
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const path = `${new Date().getFullYear()}/${crypto.randomUUID()}.${ext}`;
    const { data: signed, error } = await supabaseAdmin.storage
      .from("resumes")
      .createSignedUploadUrl(path);

    if (error || !signed) throw new Error("Não foi possível preparar o envio do currículo.");
    return { path: signed.path, token: signed.token };
  });

const applicationSchema = z.object({
  full_name: z.string().trim().min(2).max(120),
  whatsapp: z.string().trim().min(8).max(30),
  email: z.string().trim().email().max(160),
  interest_area: z.string().trim().min(2).max(80),
  preferred_unit: z.string().trim().min(2).max(80),
  availability: z.array(z.string().trim().max(40)).min(1).max(10),
  resume_path: z.string().trim().min(3).max(300),
  privacy_consent: z.literal(true),
});

export const submitJobApplication = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => applicationSchema.parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("job_applications").insert({
      full_name: data.full_name,
      whatsapp: data.whatsapp,
      email: data.email,
      interest_area: data.interest_area,
      preferred_unit: data.preferred_unit,
      availability: data.availability,
      resume_path: data.resume_path,
      privacy_consent: data.privacy_consent,
    });

    if (error) throw new Error("Não foi possível registrar a candidatura.");
    return { ok: true as const };
  });
